import React, { useState, useEffect, useCallback } from 'react';
import { Box, Paper, Typography, Button, Chip, Alert, CircularProgress } from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import ApiService from '../../../services/api.service';
import { err } from './shared';
import { can } from '../../../utils/roleUtils';

const rowCard = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
  p: 1.25, mb: 1, borderRadius: 1.5, border: '1px solid', borderColor: 'divider',
  bgcolor: 'background.paper', '&:last-of-type': { mb: 0 }, '&:hover': { boxShadow: 1 },
};

const InquiriesPage = ({ currentUser }) => {
  const isB2b = currentUser?.organizationType === 'B2B';
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ApiService.listB2bInquiries();
      setInquiries(res?.data?.data || []);
    } catch (e) { setError(err(e, 'Failed to load inquiries')); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const markContacted = async (id) => {
    try { await ApiService.markInquiryContacted(id); setSuccess('Marked as contacted'); load(); }
    catch (e) { setError(err(e, 'Failed to update inquiry')); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 0.5 }}>{isB2b ? 'Service Inquiries' : 'My Inquiries'}</Typography>
      <Typography variant="caption" color="text.secondary">
        {isB2b
          ? 'Client typing centers asking about your services — follow up offline, then mark contacted.'
          : 'Quotes you requested from providers. They contact you directly; no pricing is shown here.'}
      </Typography>

      {error && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mt: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Paper sx={{ p: 2, mt: 2, borderRadius: 2 }}>
        {inquiries.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
            {isB2b
              ? 'No inquiries yet.'
              : `You haven’t requested any quotes yet.${can(currentUser, 'connections', 'write') ? ' Use Request Quote from the Providers page.' : ''}`}
          </Typography>
        ) : isB2b ? (
          inquiries.map((i) => (
            <Box key={i.id} sx={{ ...rowCard, borderLeft: '3px solid', borderLeftColor: i.status === 'CONTACTED' ? 'success.main' : 'info.main' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{i.requester_name} — {(i.services || []).join(', ')}</Typography>
                <Typography variant="caption" color="text.secondary">{i.contact_name} · {i.contact_number}{i.notes ? ` · ${i.notes}` : ''}</Typography>
              </Box>
              {i.status !== 'SENT'
                ? <Chip label="Contacted" size="small" color="success" sx={{ flexShrink: 0 }} />
                : can(currentUser, 'connections', 'write')
                  ? <Button size="small" variant="outlined" color="info" startIcon={<SendIcon sx={{ fontSize: 16 }} />} onClick={() => markContacted(i.id)} sx={{ textTransform: 'none', flexShrink: 0 }}>Mark Contacted</Button>
                  : <Chip label="Sent" size="small" color="info" variant="outlined" sx={{ flexShrink: 0 }} />}
            </Box>
          ))
        ) : (
          inquiries.map((i) => (
            <Box key={i.id} sx={{ ...rowCard, borderLeft: '3px solid', borderLeftColor: i.status === 'CONTACTED' ? 'success.main' : 'info.main' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{i.target_name} <Typography component="span" variant="caption" color="text.secondary">({i.target_code})</Typography></Typography>
                <Typography variant="caption" color="text.secondary" display="block">{(i.services || []).join(', ')}</Typography>
                <Typography variant="caption" color="text.secondary">{i.contact_number}{i.notes ? ` · ${i.notes}` : ''}</Typography>
              </Box>
              <Chip label={i.status === 'CONTACTED' ? 'Contacted' : 'Sent'} size="small"
                color={i.status === 'CONTACTED' ? 'success' : 'info'} variant={i.status === 'CONTACTED' ? 'filled' : 'outlined'} sx={{ flexShrink: 0 }} />
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
};

export default InquiriesPage;
